import { useMemo } from 'react';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  IconButton,
  Box,
} from '@mui/material';

import { IcGPS } from '@/assets/images';
import { TableColumn, TableContainer } from '@/components';
import { IMasterName } from '@/models';
import {
  flattenObject,
  formatPhoneNumber,
  getMasterNameDisplayName,
  getMasterNamePhoneNumbersByPriority,
  getMasterNamePrimaryAddressText,
} from '@/utils';

interface MasterVehicleOwnerTableProps {
  columns: TableColumn[];
  owners: IMasterName[];
  registeredOwnerId?: string;
  hideTableHeaderDivider?: boolean;
  valueAsTooltip?: boolean;
  onSelectOwner?: (selectedOwner?: IMasterName) => void;
}

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

export const MasterVehicleOwnerTable = ({
  columns,
  owners,
  registeredOwnerId,
  hideTableHeaderDivider,
  valueAsTooltip,
  onSelectOwner,
}: MasterVehicleOwnerTableProps) => {
  const rows = useMemo(() => {
    return owners.map((owner) => flattenObject(owner));
  }, [owners]);

  const handleSelectOwner = (id: string) => {
    if (onSelectOwner) {
      const selectedOwner = owners.find(({ _id }) => _id === id);
      onSelectOwner(selectedOwner);
    }
  };

  const renderColumn = (fieldKey: string, row: Record<string, any>) => {
    const owner = owners.find(({ _id }) => _id === row._id);

    switch (fieldKey) {
      case 'action': {
        return (
          <Button
            color="error"
            onClick={() => handleSelectOwner(row._id || '')}
          >
            {row._id === registeredOwnerId ? 'Remove' : 'Use information'}
          </Button>
        );
      }
      case 'name':
        return getMasterNameDisplayName(owner);
      case 'phoneNumber': {
        const { primaryPhoneNumber } =
          getMasterNamePhoneNumbersByPriority(owner);

        return primaryPhoneNumber
          ? formatPhoneNumber(primaryPhoneNumber.phoneNumber)
          : '';
      }
      case 'addresses': {
        const addressText = getMasterNamePrimaryAddressText(owner);

        if (!addressText) {
          return '';
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '14px' }}>{addressText}</Typography>
            <Tooltip title="Open in Google Maps" placement="top">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openInGoogleMaps(addressText);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  border: '1px solid #CFD6DA',
                  backgroundColor: '#FFFFFF',
                  color: '#3E5A6A',
                  transition: 'all 0.2s ease',
                  pointerEvents: 'auto',
                }}
              >
                <IcGPS />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
      default:
        return valueAsTooltip ? (
          <Tooltip title={row[fieldKey as keyof typeof row] as string}>
            <Typography
              style={{
                fontSize: '14px',
                wordBreak: 'break-word',
                width: 125,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row[fieldKey as keyof typeof row] as string}
            </Typography>
          </Tooltip>
        ) : (
          <>{row[fieldKey as keyof typeof row] as string}</>
        );
    }
  };

  return (
    <TableContainer sx={{ padding: 0, minHeight: 130 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              borderBottom: hideTableHeaderDivider
                ? 'none !important;'
                : 'inherit',
            }}
          >
            {columns.map((field) => (
              <TableCell key={field.key}>{field.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id}>
              {columns.map((field) => (
                <TableCell key={field.key} style={{ width: 100 }}>
                  {renderColumn(field.key, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
